import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import {
  cancelComplement,
  Complement,
  ComplementMotif,
  euros,
  listComplements,
  listSavedTitles,
  messageErreur,
  MOTIF_OPTIONS,
  proposeComplement,
  saveTitle,
} from './complementApi';
import InvoiceItemsEditor, { SavedInvoiceTitle } from './InvoiceItemsEditor';
import { formatEur, invoiceTotals, InvoiceItemDraft, newItem, toQontoItems, validateItems } from './invoiceItemsMath';

/**
 * Vue Skipcar : proposer une facture complémentaire sur un dossier CG et suivre
 * celles déjà proposées (spécification « CG côté agence » v1.3, §13.8).
 */

const STATUT_LABELS: Record<
  Complement['statut'],
  { label: string; color: 'default' | 'warning' | 'info' | 'success' }
> = {
  propose: { label: "À accepter par l'agence", color: 'warning' },
  accepte: { label: 'Facture en attente', color: 'info' },
  paye: { label: 'Réglé', color: 'success' },
  annule: { label: 'Annulé', color: 'default' },
};

const TEXTES_PAR_DEFAUT: Record<ComplementMotif, string> = {
  ticket_ants:
    "Le contrôle technique n'a pas été télétransmis à l'ANTS. Nous devons ouvrir un ticket pour débloquer le dossier.",
  difference_taxes: '',
  autre: '',
};

const ligneComplement = (c: Complement, onCancel: (c: Complement) => void, busy: boolean) => (
  <Box key={c.id} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
      <Box>
        <Typography fontWeight={600}>{c.titre}</Typography>
        <Typography variant="body2" color="text.secondary">
          {euros(c.montantHtCentimes)} HT · {euros(c.montantTtcCentimes)} TTC
          {c.modeFacturation === 'releve' ? ' · sur le relevé' : ''}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          label={
            c.statut === 'accepte' && c.modeFacturation === 'releve' ? 'Sur le relevé' : STATUT_LABELS[c.statut].label
          }
          color={STATUT_LABELS[c.statut].color}
        />
        {c.facture?.url && (
          <Button size="small" href={c.facture.url} target="_blank" rel="noreferrer">
            {c.facture.numero}
          </Button>
        )}
        {c.facture && !c.facture.url && (
          <Typography variant="body2" color="text.secondary">
            {c.facture.numero}
          </Typography>
        )}
        {(c.statut === 'propose' || (c.statut === 'accepte' && !c.facture)) && (
          <Button size="small" color="error" disabled={busy} onClick={() => onCancel(c)}>
            Annuler
          </Button>
        )}
      </Stack>
    </Stack>
    <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
      {c.items.map((item, index) => (
        <li key={index}>
          <Typography variant="body2">
            {item.title} : {item.quantity} × {Number(item.unit_price.value).toLocaleString('fr-FR')} € HT, TVA{' '}
            {(Number(item.vat_rate) * 100).toLocaleString('fr-FR')} %
          </Typography>
        </li>
      ))}
    </Box>
  </Box>
);

export interface ComplementManagerDialogProps {
  open: boolean;
  onClose: () => void;
  registrationId: number;
  regPlate: string;
  /** Appelé après une proposition ou une annulation, pour rafraîchir la liste. */
  onChanged?: () => void | Promise<void>;
}

const ComplementManagerDialog = ({
  open,
  onClose,
  registrationId,
  regPlate,
  onChanged,
}: ComplementManagerDialogProps) => {
  const [complements, setComplements] = useState<Complement[]>([]);
  // La liste n'est rafraîchie qu'à la fermeture : rafraîchie tout de suite, elle
  // déplace le dossier dans une autre section et démonte cette fenêtre avant
  // que l'utilisateur voie le résultat (constaté en recette, 10/10/2026).
  const [aModifie, setAModifie] = useState(false);
  const fermer = () => {
    onClose();
    if (aModifie) {
      setAModifie(false);
      void onChanged?.();
    }
  };
  const [savedTitles, setSavedTitles] = useState<SavedInvoiceTitle[]>([]);
  const [chargement, setChargement] = useState(false);
  const [motif, setMotif] = useState<ComplementMotif>('ticket_ants');
  const [texte, setTexte] = useState(TEXTES_PAR_DEFAUT.ticket_ants);
  const [items, setItems] = useState<InvoiceItemDraft[]>([newItem()]);
  const [showErrors, setShowErrors] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const totaux = useMemo(() => invoiceTotals(items), [items]);

  const recharger = async () => {
    setChargement(true);
    try {
      const [liste, titres] = await Promise.all([listComplements(registrationId), listSavedTitles()]);
      setComplements(liste);
      setSavedTitles(titres);
    } catch (error) {
      setErreur(messageErreur(error, 'Impossible de charger les compléments du dossier.'));
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setErreur(null);
    setInfo(null);
    setShowErrors(false);
    setMotif('ticket_ants');
    setTexte(TEXTES_PAR_DEFAUT.ticket_ants);
    setItems([newItem()]);
    void recharger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, registrationId]);

  const changerMotif = (valeur: ComplementMotif) => {
    setMotif(valeur);
    // Le texte proposé par défaut ne remplace jamais un texte déjà rédigé.
    if (!texte.trim() || Object.values(TEXTES_PAR_DEFAUT).includes(texte)) setTexte(TEXTES_PAR_DEFAUT[valeur]);
  };

  const enregistrerTitre = async (saved: SavedInvoiceTitle) => {
    try {
      setSavedTitles(await saveTitle(saved));
    } catch (error) {
      setErreur(messageErreur(error, "Le titre n'a pas pu être enregistré."));
    }
  };

  const proposer = async () => {
    setShowErrors(true);
    setErreur(null);
    setInfo(null);
    if (!validateItems(items).valid) return;
    setEnvoi(true);
    try {
      const cree = await proposeComplement(registrationId, { motif, texte, items: toQontoItems(items) });
      setInfo(
        cree.statut === 'propose'
          ? "Complément proposé : l'agence est prévenue par e-mail et doit l'accepter."
          : `Complément facturé${cree.facture ? ` (${cree.facture.numero})` : ' sur le prochain relevé'}.`,
      );
      setItems([newItem()]);
      setShowErrors(false);
      await recharger();
      setAModifie(true);
    } catch (error) {
      setErreur(messageErreur(error, "Le complément n'a pas pu être proposé."));
    } finally {
      setEnvoi(false);
    }
  };

  const annuler = async (c: Complement) => {
    if (!window.confirm(`Annuler le complément « ${c.titre} » de ${euros(c.montantTtcCentimes)} TTC ?`)) return;
    setEnvoi(true);
    try {
      await cancelComplement(c.id);
      await recharger();
      setAModifie(true);
    } catch (error) {
      setErreur(messageErreur(error, "Le complément n'a pas pu être annulé."));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={open} onClose={envoi ? undefined : fermer} fullWidth maxWidth="md">
      <DialogTitle>Facture complémentaire · {regPlate}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {erreur && <Alert severity="error">{erreur}</Alert>}
          {info && <Alert severity="success">{info}</Alert>}

          {chargement ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            complements.length > 0 && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Compléments du dossier
                </Typography>
                {complements.map((c) => ligneComplement(c, annuler, envoi))}
              </Stack>
            )
          )}

          <Divider />

          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              Proposer un complément
            </Typography>
            <TextField
              select
              label="Motif"
              value={motif}
              onChange={(e) => changerMotif(e.target.value as ComplementMotif)}
              disabled={envoi}
            >
              {MOTIF_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Explication pour l'agence"
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              multiline
              minRows={2}
              inputProps={{ maxLength: 2000 }}
              disabled={envoi}
              helperText={
                motif === 'difference_taxes'
                  ? "L'agence s'y est engagée à la création : la facture part tout de suite, sans étape d'acceptation."
                  : "L'agence reçoit un e-mail et doit accepter le complément. La facture part à son accord."
              }
            />
            <InvoiceItemsEditor
              items={items}
              onChange={setItems}
              savedTitles={savedTitles}
              onSaveTitle={(saved) => void enregistrerTitre(saved)}
              disabled={envoi}
              showErrors={showErrors}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
          Total : {formatEur(totaux.totalHT)} HT · <strong>{formatEur(totaux.totalTTC)} TTC</strong>
        </Typography>
        <Button onClick={fermer} disabled={envoi}>
          Fermer
        </Button>
        <Button
          variant="contained"
          onClick={() => void proposer()}
          disabled={envoi}
          startIcon={envoi ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {motif === 'difference_taxes' ? 'Facturer la différence' : 'Proposer à l’agence'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComplementManagerDialog;
